export interface CalculateOverflowOptions {
	containerWidth: number;
	itemWidths: number[];
	triggerWidth: number;
	gap: number;
	/** Fewest items to keep visible; the rest go to the trigger. Defaults to 1; pass 0 to let every item collapse into the trigger. */
	minVisible?: number;
}

export interface CalculateOverflowResult {
	splitIndex: number;
	overflowCount: number;
}

/**
 * Two-pass algorithm: first checks if all items fit (no trigger needed),
 * then reserves trigger width and finds the split point. Keeps at least `minVisible` items.
 */
export function calculateOverflow(options: CalculateOverflowOptions): CalculateOverflowResult {
	const { containerWidth, itemWidths, triggerWidth, gap, minVisible = 1 } = options;
	const count = itemWidths.length;

	if (count === 0) {
		return { splitIndex: 0, overflowCount: 0 };
	}

	let totalWidth = 0;

	for (let i = 0; i < count; i++) {
		if (i > 0) totalWidth += gap;
		totalWidth += itemWidths[i]!;
	}

	if (totalWidth <= containerWidth) {
		return { splitIndex: count, overflowCount: 0 };
	}

	const availableWidth = containerWidth - triggerWidth;
	let usedWidth = 0;

	for (let i = 0; i < count; i++) {
		const itemWidth = itemWidths[i]!;
		const gapBefore = i > 0 ? gap : 0;

		if (usedWidth + gapBefore + itemWidth + gap <= availableWidth) {
			usedWidth += gapBefore + itemWidth;
		}
		else {
			const splitIndex = Math.max(minVisible, i);
			return { splitIndex, overflowCount: count - splitIndex };
		}
	}

	return { splitIndex: count, overflowCount: 0 };
}
